import React, { useCallback, useEffect, useRef, useState } from 'react'
import { IconContext } from 'react-icons'
import TagManager from "react-gtm-module";
import {
    ButtonContainer,
    ErrorText,
    NextButton,
    QuoteAddressWrapper,
    QuoteAppendedText,
    QuoteAutocomplete,
    QuoteEyebrow,
    QuoteFieldStack,
    QuoteGrid,
    QuoteHeader,
    QuoteHeader3,
    QuoteInnerWrapper,
    QuoteInput,
    QuoteInputDeleteIcon,
    QuoteInputLocateIcon,
    QuoteInputTitle,
    QuoteInputWrapper,
    RegistrationErrorWrapper,
} from './QuoteElements'
import { Oval } from 'react-loading-icons';
import StoryPanel from './StoryPanel';
import { SERVED_STATES } from '../../constants/servedStates';

const Locate = ({
    formState,
    setFormState,
    initialFormState,
    section,
    setSection,
    nextButton,
    updateFormControl,
    submitting,
    customerErrMesg,
    setCustomerErrMesg,
    loadingTexts,
    loadingTextIndex,
    errorFormState
}) => {
  const [addressValid, setAddressValid] = useState(true);
  const onPlaceSelected = useRef(() => undefined);

  // Shared with the panel that advertises them, so the list the form gates on
  // and the list the customer reads can't drift apart.
  const validStates = SERVED_STATES;

  const updateFormControlAddy = useCallback((event) => {
    setCustomerErrMesg('');
    if (!event || !event.address_components) return;

    let zip = '';
    let state = '';

    event.address_components.forEach(component => {
      if (component.types.includes('postal_code')) {
        zip = component.short_name;
      }
      if (component.types.includes('administrative_area_level_1')) {
        state = component.short_name;
      }
    });

    const updatedFormState = { ...formState, zip, address: event.formatted_address, state };
    setFormState(updatedFormState);
    setAddressValid(true);
  }, [formState, setFormState, setCustomerErrMesg]);

  const handleLocate = async () => {
    try{
      setAddressValid(true);
      navigator.geolocation.getCurrentPosition(function(position) {
        const geocoder = new window.google.maps.Geocoder();
        const latLng = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        geocoder.geocode({ location: latLng }, (results, status) => {
          if (status === 'OK' && results[0]) {
            updateFormControlAddy(results[0]);
          } else {
            setCustomerErrMesg('Unable to fetch location data.');
            setAddressValid(false);
          }
        });
      },
      (error) => {
        // Handle geolocation errors
        console.error("Geolocation error:", error);
        if (error.code === error.PERMISSION_DENIED) {
          setCustomerErrMesg('Location access denied. Please enable location services.');
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          setCustomerErrMesg('Location information is unavailable.');
        } else if (error.code === error.TIMEOUT) {
          setCustomerErrMesg('Location request timed out. Please try again.');
        } else {
          setCustomerErrMesg('An unknown error occurred while accessing location.');
        }
        setAddressValid(false); // Set the address as invalid
      }
    );
    }
    catch(error){
      console.error('Error fetching location:', error);
      setCustomerErrMesg('Unable to use Locate feature.');
      setAddressValid(false);
    }
  }

  const handleDelete = () => {
    setCustomerErrMesg('');
    const updatedFormState = { ...formState };
    updatedFormState['address'] = '';
    setFormState(updatedFormState);
    setAddressValid(true);
  }
  useEffect(() => {
    onPlaceSelected.current = (place) => {
      // console.log("place", place);
      if (place && place[0] && place[0].formatted_address) {
        updateFormControlAddy(place[0]); // Update address and zip
        setAddressValid(true); // Address is valid
      } else {
        setAddressValid(false); // Invalid or no selection
      }
    };
  }, [updateFormControlAddy]);

  const nextButtonLocate = () =>  {
    TagManager.dataLayer({
      gtmId: "GTM-MD88S8R",
      dataLayer: {
        event: 'locate_next_clicked'
      }
    });
    if(!validStates.includes(formState.state))
    {
      setCustomerErrMesg("Sorry, we are not available in your state yet");
      TagManager.dataLayer({
        gtmId: "GTM-MD88S8R",
        dataLayer: {
          event: 'locate_next_clicked_invalid_state'
        }
      });
    }
    else{
      nextButton();
      setCustomerErrMesg("");
    }
  }

  const handleKeyPress = (e) => {
    if(e.key === 'Enter' || e.which === 13){
      nextButtonLocate();
     }
  }

  const handleInputChange = (e) => {
    const updatedFormState = { ...formState, address: e.target.value };
    setFormState(updatedFormState);
    // console.log("inoput change", e.target.value)
    setAddressValid(false);
  };

  const handleAutoChange = (e) => {
    // const updatedFormState = { ...formState, address: e.target.value };
    // setFormState(updatedFormState);
    // console.log("inoput change", e.target.value)
    setAddressValid(false);
  };

  return (
    <QuoteGrid>
      <StoryPanel step="locate" />
      <QuoteInnerWrapper>
        <QuoteEyebrow>Step 1 of 3</QuoteEyebrow>
        <QuoteHeader>Start with your home</QuoteHeader>
        <QuoteHeader3>
          Your address and what you spend on power. That's everything we need to size a system.
        </QuoteHeader3>
        <QuoteFieldStack>
        <QuoteInputWrapper>
        <QuoteInputTitle htmlFor='address'>Home Address</QuoteInputTitle>
        <QuoteAddressWrapper>
          <IconContext.Provider
              value={{ size: '20px' }}
          >
          {formState.address === '' ? (
              <>
                <QuoteAutocomplete 
                  apiKey={process.env.REACT_APP_GOOGLE_MAPS_API}
                  onPlaceSelected={(...args) => onPlaceSelected.current(args)}
                  options={{
                    types: ["address"],
                    componentRestrictions: { country: "us" },
                  }}
                  id='address'
                  defaultValue={formState.address}
                  onChange={handleAutoChange}
                  onBlur={() => {
                    if (!addressValid) {
                      setCustomerErrMesg(
                        'Please select a valid address from the dropdown or use Locate.'
                      );
                    }
                  }}
                />
                <QuoteInputLocateIcon onClick={handleLocate} />
              </>
            ) : (
              <>
                <QuoteInput 
                  type='text' 
                  id='address' 
                  value={formState.address} 
                  onChange={handleInputChange}
                  onBlur={() => {
                    if (!addressValid) {
                      setCustomerErrMesg(
                        'Please select a valid address from the dropdown or use Locate.'
                      );
                    }
                  }}
                />
                <QuoteInputDeleteIcon onClick={handleDelete} />
              </>
            )}
          
          </IconContext.Provider>
          
        </QuoteAddressWrapper>
        {!addressValid && (
            <ErrorText>{customerErrMesg}</ErrorText>
          )}
        </QuoteInputWrapper>
        <QuoteInputWrapper>
            <QuoteInputTitle htmlFor='electricBill'>Average Electric Bill</QuoteInputTitle>
            <QuoteAddressWrapper>
              <QuoteInput
                type='text'
                id='electricBill'
                inputMode='numeric'
                value={formState.electricBill}
                onChange={(updateFormControl)}
                onKeyPress={handleKeyPress}
              />
              <QuoteAppendedText>/ mo</QuoteAppendedText>
            </QuoteAddressWrapper>
            {errorFormState?.billCapped && (
              <ErrorText>
                We cap online estimates at $800 a month. We've set it to $800 — for a bill this
                size, give us a call and we'll size the system with you.
              </ErrorText>
            )}
        </QuoteInputWrapper>
        </QuoteFieldStack>
        {/* The "Select Your Energy Solutions" cards used to sit here. They asked
            the customer to commit to solar / battery / EV charger before they
            had seen a single number, and nothing downstream needs the answer any
            more: the Design page picks the package and its sliders own the
            battery and charger counts, keeping `solutions` in step themselves. */}
        {!submitting&&
          <ButtonContainer>
            <NextButton onClick={nextButtonLocate} disabled={!formState.address || !formState.electricBill || !addressValid} >Next</NextButton>
          </ButtonContainer>
        }
        {submitting &&
          <>
            <ButtonContainer>
              <NextButton>
                <Oval height='1.5rem'/>
              </NextButton>
            </ButtonContainer>
            <RegistrationErrorWrapper>
              {loadingTexts[loadingTextIndex]}
            </RegistrationErrorWrapper>

          </>
        }
        {customerErrMesg &&
          <RegistrationErrorWrapper>
              <ErrorText>{customerErrMesg}</ErrorText>
          </RegistrationErrorWrapper>
        }
          
      </QuoteInnerWrapper>
    </QuoteGrid>
    )
}

export default Locate